import React, { useState, useEffect } from 'react';
import { Info, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { integrationService } from '../../services/integrationService';

const WordPressForm = ({ formData, setFormData, isSubmitting, handleCreateIntegration }) => {
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  // Function to verify WordPress credentials
  const verifyCredentials = async () => {
    if (!formData.wordpress_url || !formData.username || !formData.app_password) {
      return;
    }

    setVerifying(true);
    setVerificationResult(null);

    try {
      const result = await integrationService.verifyWordPressCredentials({
        wordpress_url: formData.wordpress_url,
        username: formData.username,
        app_password: formData.app_password
      });

      setVerificationResult(result);
    } catch (error) {
      setVerificationResult({
        valid: false,
        error: error.message || 'Verification failed'
      });
    } finally {
      setVerifying(false);
    }
  };

  // Reset verification when credentials change
  useEffect(() => {
    setVerificationResult(null);
  }, [formData.wordpress_url, formData.username, formData.app_password]);

  return (
    <form onSubmit={handleCreateIntegration} className="space-y-6">
      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Integration Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md p-2"
          placeholder="My WordPress Website"
          value={formData.name || ''}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            name: e.target.value
          }))}
          required
        />
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          WordPress URL <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            className={`w-full border ${verificationResult?.valid === false ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 pr-8`}
            placeholder="https://mywebsite.com"
            value={formData.wordpress_url || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              wordpress_url: e.target.value
            }))}
            required
          />
          {verificationResult?.valid && (
            <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" size={18} />
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Enter the full URL of your WordPress site (e.g., https://example.com)
        </p>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Username <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            className={`w-full border ${verificationResult?.valid === false ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 pr-8`}
            placeholder="admin@email.com"
            value={formData.username || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              username: e.target.value
            }))}
            required
          />
          {verificationResult?.valid && (
            <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" size={18} />
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Your WordPress username (not email)
        </p>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Application Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="password"
            className={`w-full border ${verificationResult?.valid === false ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 pr-8`}
            placeholder="Your Application Password"
            value={formData.app_password || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              app_password: e.target.value
            }))}
            required
          />
          {verificationResult?.valid && (
            <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" size={18} />
          )}
        </div>
        <div className="text-sm text-gray-600 mt-1 flex items-center">
          <Info size={16} className="mr-1" />
          <a
            href="https://wordpress.org/documentation/article/application-passwords/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline flex items-center"
          >
            Learn how to create an Application Password <ExternalLink size={14} className="ml-1" />
          </a>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Important: This must be an Application Password, not your regular login password
        </p>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Publish Status <span className="text-red-500">*</span>
        </label>
        <select
          className="w-full border border-gray-300 rounded-md p-2"
          value={formData.publish_status || 'publish'}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            publish_status: e.target.value
          }))}
          required
        >
          <option value="publish">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={verifyCredentials}
          disabled={verifying || !formData.wordpress_url || !formData.username || !formData.app_password}
          className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition font-medium disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {verifying ? (
            <span className="flex items-center">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
              Verifying...
            </span>
          ) : 'Verify Credentials'}
        </button>

        {verificationResult?.valid && (
          <div className="mt-2 text-green-600 flex items-center">
            <CheckCircle size={16} className="mr-1" />
            <div>
              <p>Credentials verified successfully</p>
              {verificationResult.user && (
                <p className="text-sm">Connected as: {verificationResult.user.name}</p>
              )}
            </div>
          </div>
        )}

        {verificationResult?.valid === false && (
          <div className="mt-2 text-red-600 flex items-center">
            <AlertCircle size={16} className="mr-1" />
            <div>
              <p>{verificationResult.error || 'Verification failed'}</p>
              {verificationResult.error?.includes('Application Password') && (
                <p className="text-sm mt-1">
                  Application passwords are different from your login password.
                  They can be created in your WordPress admin under Users → Profile → Application Passwords.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default WordPressForm;

